import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useChekinSDK} from 'context/ChekinSDK';
import {useCustomTheme} from 'context/customTheme';
import {useSummary} from 'context/summary';
import {toastResponseError} from 'utils/common';
import {LocalStorage, SessionStorage} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {PREVIOUS_PAGE_STORAGE_KEY} from '../utils/constants';
import {environments} from '../configs';
import {useIsMounted} from '@chekin/guestapp-ui-kit';

const makeToString = (to?: string) => {
  if (!to) {
    return '';
  }

  return to.startsWith('/') ? to : `/${to}`;
};

function usePath() {
  const {mainToken} = useChekinSDK();
  const mainPart = environments.VITE_APP_IS_SDK ? '' : `/${mainToken}`;

  const getIVPath = useCallback(
    (to: string) => {
      const ivRoute =
        to === PATHS.verification.main ? to : `${PATHS.verification.main}/${to}`;
      return `${mainPart}/${ivRoute}`;
    },
    [mainPart],
  );

  const getTaxesPath = useCallback(
    (to: string) => {
      const ivRoute = to === PATHS.taxes.main ? to : `${PATHS.taxes.main}/${to}`;
      return `${mainPart}/${ivRoute}`;
    },
    [mainPart],
  );

  const getAuthPath = useCallback(
    (to: string) => {
      const authRoute = to === PATHS.auth.root ? to : `${PATHS.auth.root}/${to}`;
      return `${mainPart}/${authRoute}`;
    },
    [mainPart],
  );

  const getCheckoutPath = useCallback(
    (to: string) => {
      const authRoute = to === PATHS.checkout.main ? to : `${PATHS.checkout.main}/${to}`;
      return `${mainPart}/${authRoute}`;
    },
    [mainPart],
  );

  const getOnboardingPath = useCallback(
    (to: string) => {
      const onboardingRoute =
        to === PATHS.onboarding.root ? to : `${PATHS.onboarding.root}/${to}`;
      return `${mainPart}/${onboardingRoute}`;
    },
    [mainPart],
  );

  const getMainPath = useCallback(
    (to = '') => {
      if (to.includes('token')) {
        return `/${mainToken || ''}`;
      }
      const toString = makeToString(to);

      return `${mainPart}${toString}`;
    },
    [mainPart, mainToken],
  );

  const getPaymentsPath = useCallback(
    (to: string) => {
      const paymentsRoute =
        to === PATHS.payments.main ? to : `${PATHS.payments.main}/${to}`;
      return `${mainPart}/${paymentsRoute}`;
    },
    [mainPart],
  );

  const getSearchReservationPath = useCallback(
    (to: string) => {
      return `${mainPart}/${to}`;
    },
    [mainPart],
  );

  const getFullPath = useCallback(
    (to: string) => {
      const ivPathsArray = Object.values(PATHS.verification);
      const taxesPathsArray = Object.values(PATHS.taxes);
      const paymentsPathsArray = Object.values(PATHS.payments);
      const authPathsArray = Object.values(PATHS.auth);
      const onboardingPathsArray = Object.values(PATHS.onboarding);
      const checkoutPathsArray = Object.values(PATHS.checkout);
      const searchReservationPathsArray = Object.values(PATHS.searchReservation);
      const isPaymentsPath = paymentsPathsArray.includes(to);
      const isIVPath = ivPathsArray.includes(to);
      const isTaxesPath = taxesPathsArray.includes(to);
      const isAuthPath = authPathsArray.includes(to);
      const isOnboardingPath = onboardingPathsArray.includes(to);
      const isCheckoutPath = checkoutPathsArray.includes(to);
      const isSearchReservationPath = searchReservationPathsArray.includes(to);
      if (isIVPath) {
        return getIVPath(to);
      } else if (isTaxesPath) {
        return getTaxesPath(to);
      } else if (isPaymentsPath) {
        return getPaymentsPath(to);
      } else if (isAuthPath) {
        return getAuthPath(to);
      } else if (isOnboardingPath) {
        return getOnboardingPath(to);
      } else if (isCheckoutPath) {
        return getCheckoutPath(to);
      } else if (isSearchReservationPath) {
        return getSearchReservationPath(to);
      } else {
        return getMainPath(to);
      }
    },
    [
      getIVPath,
      getMainPath,
      getTaxesPath,
      getPaymentsPath,
      getAuthPath,
      getOnboardingPath,
      getCheckoutPath,
      getSearchReservationPath,
    ],
  );

  return {
    getIVPath,
    getMainPath,
    getFullPath,
    getAuthPath,
    getCheckoutPath,
    getSearchReservationPath,
  };
}

const buildLocalStorageData = (id: string, showed: boolean) => ({id, showed});

type useModalOncePerSessionProps = {
  id?: string;
  storageKey?: string;
  storageType?: 'Local' | 'Session';
  trigger?: 'auto' | 'manual';
};
function useModalOncePerSession({
  id,
  storageKey,
  storageType = 'Local',
  trigger = 'auto',
}: useModalOncePerSessionProps) {
  const [key, setKey] = useState(storageKey && id ? `${storageKey}-${id}` : '');
  const Storage = storageType === 'Local' ? LocalStorage : SessionStorage;
  const {isTemplateInitialized} = useCustomTheme();
  const {isSuccess} = useSummary();

  const shouldOpenModal = useMemo(() => {
    if (isTemplateInitialized && key && isSuccess && id) {
      const alreadyOpenModal =
        Storage.get<{id?: string; showed?: boolean}>(key) ||
        buildLocalStorageData(id, false);

      const shouldSetStorage =
        (alreadyOpenModal?.id !== id || !alreadyOpenModal?.showed) && trigger === 'auto';
      if (shouldSetStorage) {
        Storage.set(key, buildLocalStorageData(id, true));
        return true;
      }
    }

    return false;
  }, [key, id, Storage, isTemplateInitialized, isSuccess, trigger]);

  const setStorageKey = useCallback((storageKey: string) => {
    setKey(storageKey);
  }, []);

  const toggleStorageKeyData = useCallback(
    (showed: boolean) => {
      if (!id) return;
      Storage.set(key, buildLocalStorageData(id, showed));
    },
    [key, id, Storage],
  );

  return {shouldOpenModal, setStorageKey, toggleStorageKeyData};
}

function useSetPreviousPage() {
  const location = useLocation();

  useEffect(() => {
    return () => {
      SessionStorage.set(PREVIOUS_PAGE_STORAGE_KEY, location.pathname);
    };
  }, [location.pathname]);
}

type useErrorToastOptions = {
  notFoundMessage?: string;
  onlyMounted?: boolean;
};

function useErrorToast(error: unknown, options?: useErrorToastOptions) {
  const notFoundMessage = options?.notFoundMessage;
  const onlyMounted = options?.onlyMounted;
  const isMounted = useIsMounted();

  useEffect(() => {
    if (error) {
      if (
        (error as {body: {status_code: number}})?.body?.status_code === 404 &&
        notFoundMessage
      ) {
        (error as {message: string}).message = notFoundMessage;
      }

      if (isMounted.current || !onlyMounted) {
        toastResponseError(error);
      }
    }
  }, [error, notFoundMessage, onlyMounted, isMounted]);
}

export {
  usePath,
  useModalOncePerSession,
  useSetPreviousPage,
  useErrorToast,
  buildLocalStorageData,
};
